import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import {
  Cpu, Brain, BarChart2, TrendingUp, Cloud, AlertTriangle,
  Shield, PieChart, Target, Swords, Zap,
  Activity, CheckCircle, AlertCircle, Server, Database, Radio,
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { healthApi, oddsApi } from '../../api/client';
import { OrchestrationFlowVisualizer } from '../../components/agent/OrchestrationFlowVisualizer';
import { formatDistanceToNow } from 'date-fns';
import type { AgentActivityEvent } from '../../types';
import type { ProviderHealth } from '../../api/client';

// ── Agent definitions ─────────────────────────────────────────────────────────
const AGENT_DEFS = [
  { key: 'matchup',    label: 'Matchup Analysis',    abbr: 'MTH', icon: Swords,        color: 'text-brand',         bg: 'bg-brand/10 border-brand/20',     description: 'Pitcher quality, lineup depth, H2H matchup data',         weight: 0.25 },
  { key: 'market',     label: 'Market Intelligence', abbr: 'MKT', icon: BarChart2,     color: 'text-warn',           bg: 'bg-warn/10 border-warn/20',       description: 'Odds movement, line efficiency, sharp money signals',      weight: 0.20 },
  { key: 'ml',         label: 'ML Model',            abbr: 'ML',  icon: Brain,         color: 'text-purple-bright',  bg: 'bg-purple/10 border-purple/20',   description: 'Logistic regression ensemble probability model',           weight: 0.20 },
  { key: 'form',       label: 'Recent Form',         abbr: 'FRM', icon: TrendingUp,    color: 'text-win',            bg: 'bg-win/10 border-win/20',         description: 'L10/L5 win rates, run differential, momentum',            weight: 0.15 },
  { key: 'portfolio',  label: 'Portfolio Gate',      abbr: 'PRT', icon: PieChart,      color: 'text-brand',          bg: 'bg-brand/10 border-brand/20',     description: 'Cross-bet correlation, exposure diversity, Kelly sizing',  weight: 0.15 },
  { key: 'risk',       label: 'Risk Gate',           abbr: 'RSK', icon: AlertTriangle, color: 'text-warn',           bg: 'bg-warn/10 border-warn/20',       description: 'Daily loss limits, bankroll concentration risk',           weight: 0.15 },
  { key: 'context',    label: 'Context & Conditions',abbr: 'CTX', icon: Cloud,         color: 'text-info',           bg: 'bg-info/10 border-info/20',       description: 'Weather impact, park factors, travel fatigue, umpires',   weight: 0.00 },
  { key: 'clv',        label: 'CLV Edge',            abbr: 'CLV', icon: Target,        color: 'text-gold',           bg: 'bg-gold/10 border-gold/20',       description: 'Closing line value — how well we beat the closing number', weight: 0.05 },
  { key: 'validation', label: 'Adversarial Review',  abbr: 'VAL', icon: Shield,        color: 'text-loss',           bg: 'bg-loss/10 border-loss/20',       description: 'Adversarial challenges — asks "why should we NOT bet this?"', weight: 0.00 },
];

// ── Agent card ────────────────────────────────────────────────────────────────
function AgentCard({ def, events }: { def: typeof AGENT_DEFS[0]; events: AgentActivityEvent[] }) {
  const Icon = def.icon;
  const recentCutoff = Date.now() - 5 * 60 * 1000;
  const recentEvents = events.filter((e) => new Date(e.timestamp).getTime() > recentCutoff);
  const isActive = recentEvents.length > 0;
  const lastEvent = events[0];
  const highPriorityCount = events.filter((e) => e.severity === 'high').length;

  const avgDelta =
    events.length > 0
      ? events
          .filter((e) => typeof e.confidenceDelta === 'number')
          .reduce((sum, e) => sum + (e.confidenceDelta ?? 0), 0) /
          Math.max(1, events.filter((e) => typeof e.confidenceDelta === 'number').length)
      : null;

  return (
    <motion.div
      layout
      className={clsx(
        'flex flex-col gap-3 p-3.5 rounded-xl border transition-all duration-300',
        isActive ? `${def.bg} module-active` : 'border-surface-4 bg-surface-1',
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-2.5">
        <div className={clsx('flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg border', def.bg)}>
          <Icon className={clsx('w-4 h-4', def.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={clsx('text-xs font-bold leading-tight', def.color)}>{def.label}</p>
            <span className="text-[8px] font-mono font-bold text-gray-600 bg-surface-3 px-1 py-px rounded">{def.abbr}</span>
          </div>
          <p className="text-[9px] text-gray-600 leading-tight mt-0.5">{def.description}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {isActive ? (
            <>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-purple" />
              </span>
              <span className="text-[8px] text-purple/70 font-bold uppercase tracking-wide">Active</span>
            </>
          ) : (
            <>
              <span className="h-2 w-2 rounded-full bg-surface-3" />
              <span className="text-[8px] text-gray-600 font-bold uppercase tracking-wide">Idle</span>
            </>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-[8px] text-gray-600 uppercase tracking-wide">Weight</span>
          <span className="text-[11px] font-mono font-bold text-gray-300">{(def.weight * 100).toFixed(0)}%</span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-[8px] text-gray-600 uppercase tracking-wide">Events</span>
          <span className="text-[11px] font-mono font-bold text-gray-300">{events.length}</span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-[8px] text-gray-600 uppercase tracking-wide">Avg Δ</span>
          <span className={clsx(
            'text-[11px] font-mono font-bold',
            avgDelta === null ? 'text-gray-600'
            : avgDelta > 0 ? 'text-win'
            : avgDelta < 0 ? 'text-loss'
            : 'text-gray-500',
          )}>
            {avgDelta !== null ? `${avgDelta >= 0 ? '+' : ''}${(avgDelta * 100).toFixed(1)}%` : '—'}
          </span>
        </div>
      </div>

      {/* Last event */}
      {lastEvent && (
        <div className="pt-2 border-t border-surface-4/60">
          <p className="text-[9px] text-gray-500 leading-snug line-clamp-2">{lastEvent.action}</p>
          <span className="text-[8px] text-gray-600 tabular-nums">
            {formatDistanceToNow(new Date(lastEvent.timestamp), { addSuffix: true })}
          </span>
        </div>
      )}

      {highPriorityCount > 0 && (
        <div className="flex items-center gap-1">
          <AlertCircle className="w-3 h-3 text-loss flex-shrink-0" />
          <span className="text-[9px] text-loss font-medium">{highPriorityCount} high-priority event{highPriorityCount !== 1 ? 's' : ''}</span>
        </div>
      )}
    </motion.div>
  );
}

// ── Health service row ────────────────────────────────────────────────────────

function ServiceRow({
  icon: Icon, label, status, detail,
}: { icon: React.FC<{ className?: string }>; label: string; status: 'ok' | 'degraded' | 'down' | 'unknown'; detail?: string }) {
  const colors = { ok: 'text-win', degraded: 'text-warn', down: 'text-loss', unknown: 'text-gray-500' };
  const dots = { ok: 'bg-win', degraded: 'bg-warn animate-pulse', down: 'bg-loss', unknown: 'bg-gray-600' };
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-surface-4/60 last:border-0">
      <div className="w-7 h-7 rounded-lg bg-surface-2 border border-surface-4 flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-300">{label}</p>
        {detail && <p className="text-[9px] text-gray-600 truncate">{detail}</p>}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', dots[status])} />
        <span className={clsx('text-[10px] font-semibold capitalize', colors[status])}>
          {status === 'ok' ? 'Healthy' : status === 'unknown' ? 'Unknown' : status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>
    </div>
  );
}

// ── AgentObservatoryWorkspace ─────────────────────────────────────────────────
export function AgentObservatoryWorkspace() {
  const { agentActivity, recommendations, connectionStatus, systemHealth, games } = useAppStore();
  const isConnected = connectionStatus === 'connected';

  const [tab, setTab] = useState<'agents' | 'pipeline' | 'health'>('agents');
  const [health, setHealth] = useState<{ status: string; timestamp: string } | null>(null);
  const [providers, setProviders] = useState<ProviderHealth[]>([]);
  const [healthLoading, setHealthLoading] = useState(true);

  useEffect(() => {
    healthApi.check().then(setHealth).catch(() => setHealth(null));
    oddsApi
      .getHealth()
      .then((data) => { setProviders(data.providers); })
      .catch(() => {})
      .finally(() => setHealthLoading(false));
  }, []);

  const totalRecs = recommendations.length;
  const strongBets = recommendations.filter((r) => r.decisionStatus === 'STRONG_BET').length;
  const avgConf =
    recommendations.length > 0
      ? recommendations.reduce((s, r) => s + (r.confidence ?? 0), 0) / recommendations.length
      : null;

  const agentEventsByKey = AGENT_DEFS.reduce<Record<string, AgentActivityEvent[]>>((acc, def) => {
    acc[def.key] = agentActivity.filter((e) => e.agent === def.key);
    return acc;
  }, {});

  const totalEvents = agentActivity.length;
  const highPriority = agentActivity.filter((e) => e.severity === 'high').length;
  const recentCutoff = Date.now() - 5 * 60 * 1000;
  const activeAgentCount = AGENT_DEFS.filter(
    (def) => agentActivity.some((e) => e.agent === def.key && new Date(e.timestamp).getTime() > recentCutoff),
  ).length;

  const gameCount = games?.length ?? 0;

  const backendStatus: 'ok' | 'degraded' | 'down' | 'unknown' = healthLoading
    ? 'unknown'
    : health?.status === 'ok' || health?.status === 'healthy'
    ? 'ok'
    : health
    ? 'degraded'
    : 'down';

  const sseStatus: 'ok' | 'degraded' | 'down' | 'unknown' = isConnected ? 'ok' : 'down';

  return (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-surface-4 bg-surface-1/50 flex-shrink-0">
        <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-info/10 border border-info/20 flex-shrink-0">
          <Cpu className="w-3.5 h-3.5 text-info" />
        </div>
        <div>
          <p className="text-sm font-bold text-gray-100 leading-tight">Agent Observatory</p>
          <p className="text-[9px] text-gray-500 uppercase tracking-widest leading-tight">Orchestration · Health · Model Telemetry</p>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className={clsx(
            'flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold',
            isConnected ? 'text-win' : 'text-gray-500',
          )}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', isConnected ? 'bg-win animate-pulse-slow' : 'bg-gray-600')} />
            {systemHealth?.status === 'healthy' ? 'All Systems Healthy' : systemHealth?.status === 'degraded' ? 'Degraded' : 'System Down'}
          </span>
        </div>
      </div>

      {/* KPI strip */}
      <div className="flex items-stretch border-b border-surface-4 flex-shrink-0 bg-surface-1/30">
        {[
          { label: 'Active Agents',  value: `${activeAgentCount} / 9`,     color: activeAgentCount > 0 ? 'text-purple-bright' : 'text-gray-500' },
          { label: 'Total Events',   value: String(totalEvents),           color: 'text-gray-200' },
          { label: 'High Priority',  value: highPriority > 0 ? String(highPriority) : '—', color: highPriority > 0 ? 'text-loss' : 'text-gray-500' },
          { label: "Today's Recs",   value: String(totalRecs),             color: totalRecs > 0 ? 'text-brand' : 'text-gray-500' },
          { label: 'Strong Bets',    value: strongBets > 0 ? String(strongBets) : '—', color: strongBets > 0 ? 'text-gold-bright' : 'text-gray-500' },
          { label: 'Avg Confidence', value: avgConf !== null ? `${(avgConf * 100).toFixed(0)}%` : '—', color: avgConf !== null ? (avgConf >= 0.7 ? 'text-win' : avgConf >= 0.55 ? 'text-warn' : 'text-gray-400') : 'text-gray-500' },
        ].map(({ label, value, color }) => (
          <div key={label} className="flex-1 flex flex-col gap-1 px-3 py-2 border-r border-surface-4 last:border-r-0 min-w-0">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-600 leading-none select-none">{label}</span>
            <span className={clsx('text-base font-bold font-mono tabular-nums leading-none', color)}>{value}</span>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-0 border-b border-surface-4 flex-shrink-0 bg-surface-1/20">
        {([
          { key: 'agents', label: 'Agent Status' },
          { key: 'pipeline', label: 'Pipeline' },
          { key: 'health', label: 'System Health' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={clsx(
              'px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest border-b-2 transition-colors',
              tab === key
                ? 'border-info text-info'
                : 'border-transparent text-gray-500 hover:text-gray-300',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto min-h-0">

        {/* ── Agents tab ───────────────────────────────────────────────────── */}
        {tab === 'agents' && (
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {AGENT_DEFS.map((def) => (
                <AgentCard key={def.key} def={def} events={agentEventsByKey[def.key] ?? []} />
              ))}
            </div>
          </div>
        )}

        {/* ── Pipeline tab ─────────────────────────────────────────────────── */}
        {tab === 'pipeline' && (
          <div className="p-4 space-y-6">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-600 mb-3">Recommendation Pipeline</p>
              <OrchestrationFlowVisualizer
                activeAgentCount={activeAgentCount}
                totalRecs={totalRecs}
                strongBets={strongBets}
                avgConfidence={avgConf}
                isConnected={isConnected}
                gameCount={gameCount}
              />
            </div>

            {/* Recent agent events */}
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-600 mb-3">Recent Activity ({agentActivity.length} events)</p>
              {agentActivity.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-xs text-gray-600">
                  No agent activity yet. Events will appear here as agents process games.
                </div>
              ) : (
                <div className="space-y-1">
                  {agentActivity.slice(0, 30).map((event, i) => {
                    const def = AGENT_DEFS.find((d) => d.key === event.agent);
                    return (
                      <div
                        key={i}
                        className={clsx(
                          'flex items-start gap-2.5 px-3 py-2 rounded-lg border transition-colors',
                          event.severity === 'high' ? 'border-loss/25 bg-loss/5' : 'border-surface-4/60 bg-surface-1/30',
                        )}
                      >
                        <div className={clsx(
                          'w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5',
                          def?.bg ?? 'bg-surface-2 border border-surface-4',
                        )}>
                          {def && <def.icon className={clsx('w-3 h-3', def.color)} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={clsx('text-[9px] font-bold uppercase tracking-wide', def?.color ?? 'text-gray-500')}>{event.agent}</span>
                            {event.severity === 'high' && <AlertCircle className="w-2.5 h-2.5 text-loss" />}
                          </div>
                          <p className="text-[10px] text-gray-400 leading-snug">{event.action}</p>
                        </div>
                        <span className="text-[8px] text-gray-600 font-mono tabular-nums flex-shrink-0">
                          {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Health tab ───────────────────────────────────────────────────── */}
        {tab === 'health' && (
          <div className="p-4 space-y-4">
            {/* Core services */}
            <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
                <Server className="w-3.5 h-3.5 text-gray-500" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Platform Services</p>
              </div>
              <ServiceRow
                icon={Server}
                label="Backend API"
                status={backendStatus}
                detail={health?.timestamp ? `Last checked ${formatDistanceToNow(new Date(health.timestamp), { addSuffix: true })}` : undefined}
              />
              <ServiceRow
                icon={Radio}
                label="Live Feed (SSE)"
                status={sseStatus}
                detail={isConnected ? 'Real-time event stream active' : 'Reconnecting…'}
              />
            </div>

            {/* Odds providers */}
            <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
                <Database className="w-3.5 h-3.5 text-gray-500" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Odds Providers</p>
                <span className="ml-auto text-[9px] font-mono text-gray-600">
                  {providers.filter((p) => (p.successRate ?? 0) > 0).length}/{providers.length} active
                </span>
              </div>
              {healthLoading ? (
                <div className="flex items-center justify-center py-6 text-xs text-gray-600">Checking providers…</div>
              ) : providers.length === 0 ? (
                <div className="flex items-center justify-center py-6 text-xs text-gray-600">No provider data available.</div>
              ) : (
                providers.map((p) => {
                  const isActive = (p.successRate ?? 0) > 0;
                  return (
                    <ServiceRow
                      key={p.name}
                      icon={BarChart2}
                      label={p.name}
                      status={isActive ? 'ok' : p.lastFetchAt ? 'degraded' : 'down'}
                      detail={p.lastFetchAt ? `Updated ${formatDistanceToNow(new Date(p.lastFetchAt), { addSuffix: true })}` : p.lastError ?? undefined}
                    />
                  );
                })
              )}
            </div>

            {/* Module status */}
            <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
                <Activity className="w-3.5 h-3.5 text-gray-500" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Intelligence Modules</p>
              </div>
              {[
                { label: 'DiamondEdge', sport: 'MLB', active: true },
                { label: 'CourtEdge', sport: 'WNBA', active: true },
                { label: 'GridironEdge', sport: 'NFL', active: false },
                { label: 'IceEdge', sport: 'NHL', active: false },
                { label: 'PitchEdge', sport: 'Soccer', active: false },
                { label: 'OctagonEdge', sport: 'MMA', active: false },
              ].map(({ label, sport, active }) => (
                <div key={label} className="flex items-center gap-3 px-3 py-2.5 border-b border-surface-4/60 last:border-0">
                  <div className="w-7 h-7 rounded-lg bg-surface-2 border border-surface-4 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-3.5 h-3.5 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-300">{label}</p>
                    <p className="text-[9px] text-gray-600">{sport}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={clsx('w-1.5 h-1.5 rounded-full', active ? 'bg-win' : 'bg-surface-4')} />
                    <span className={clsx('text-[10px] font-semibold', active ? 'text-win' : 'text-gray-600')}>
                      {active ? 'Live' : 'Coming Soon'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
